/**
 * Valoraciones (1.0 AdminReviews): las 200 últimas con autor, valorado, estrellas y texto;
 * ocultar/restaurar (admin-moderateReview recalcula la media del valorado y audita).
 */
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { api } from '@nuvvu/shared/api';
import { call, errorText } from '../api.js';
import { db } from '../firebase.js';
import { btn, card, field, muted, Notice, str, when } from '../lib/ui.js';

type Doc = Record<string, unknown>;

export function ReviewsPage(): ReactElement {
  const [rows, setRows] = useState<Doc[] | null>(null);
  const [filter, setFilter] = useState<'all' | 'hidden' | 'low'>('all');
  const [reason, setReason] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = (): void => {
    getDocs(query(collection(db, 'reviews'), orderBy('created_at', 'desc'), limit(200)))
      .then((s) => {
        setRows(s.docs.map((d) => ({ id: d.id, ...d.data() })));
      })
      .catch(() => {
        setRows([]);
      });
  };
  useEffect(load, []);
  const moderate = (r: Doc, hidden: boolean): void => {
    setError(null);
    call(api.admin.moderateReview, {
      review_id: str(r.id),
      hidden,
      ...(reason.trim() ? { reason: reason.trim() } : {}),
    })
      .then(() => {
        setNotice(hidden ? 'Valoración oculta.' : 'Valoración restaurada.');
        load();
      })
      .catch((e: unknown) => {
        setError(errorText(e));
      });
  };
  const shown = (rows ?? []).filter((r) =>
    filter === 'hidden' ? r.hidden === true : filter === 'low' ? Number(r.rating) <= 2 : true,
  );
  return (
    <>
      <h1 style={{ margin: 0 }}>Valoraciones</h1>
      <p style={muted}>
        Las valoraciones nacen de pedidos reales y no se borran: se ocultan (desaparecen del perfil
        y de la media) y se pueden restaurar. Todo queda en el auditLog.
      </p>
      <Notice text={notice} />
      <Notice text={error} error />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {(['all', 'low', 'hidden'] as const).map((f) => (
          <button
            key={f}
            type="button"
            style={btn(filter === f)}
            onClick={() => {
              setFilter(f);
            }}
          >
            {f === 'all' ? 'Todas' : f === 'low' ? '≤ 2 estrellas' : 'Ocultas'}
          </button>
        ))}
        <input
          style={{ ...field, maxWidth: 320 }}
          placeholder="Motivo (va al auditLog)"
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
          }}
        />
      </div>
      {!rows && <p style={muted}>Cargando…</p>}
      {rows && shown.length === 0 && <p style={muted}>Sin valoraciones.</p>}
      {shown.map((r) => (
        <section key={str(r.id)} style={{ ...card, opacity: r.hidden === true ? 0.6 : 1 }}>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
            <strong>{'★'.repeat(Number(r.rating) || 0)}</strong>
            <span style={muted}>
              {str(r.role) === 'buyer' ? 'comprador' : 'vendedor'} ·{' '}
              <Link to={`/usuarios/${str(r.author_id)}`}>autor</Link> →{' '}
              <Link to={`/usuarios/${str(r.target_id)}`}>valorado</Link> · pedido{' '}
              <Link to={`/pedidos/${str(r.order_id)}`}>
                {str(r.order_id).slice(-6).toUpperCase()}
              </Link>{' '}
              · {when(r.created_at)}
              {r.automatic === true ? ' · automática' : ''}
              {r.hidden === true ? ` · OCULTA (${str(r.hidden_reason) || 'sin motivo'})` : ''}
            </span>
            <button
              type="button"
              style={{ ...btn(false, r.hidden !== true), marginLeft: 'auto' }}
              onClick={() => {
                moderate(r, r.hidden !== true);
              }}
            >
              {r.hidden === true ? 'Restaurar' : 'Ocultar'}
            </button>
          </div>
          {str(r.product_title) && <span style={muted}>{str(r.product_title)}</span>}
          <p style={{ margin: 0, fontSize: 13, whiteSpace: 'pre-wrap' }}>{str(r.text) || '—'}</p>
        </section>
      ))}
    </>
  );
}
